/**
 * CLI OAuth handoff RPC (unauthenticated).
 *
 * The device-code-style bridge between a headless CLI and the authenticated web
 * app. The CLI has no credentials yet, so both procedures are public:
 *
 *   - initiate — register a pending handoff (bound to a PKCE challenge) and
 *     return the browser URL the user opens to authorize.
 *   - poll     — exchange the handoff code + PKCE verifier for the minted
 *     session, once the browser flow has completed.
 *
 * Security rests on the PKCE binding: the browser-visible `handoffCode` is not a
 * bearer secret — the session token is released only to the holder of the
 * `codeVerifier` whose SHA-256 matches the stored challenge. See the worker
 * handler and `worker/lib/pkce.ts`.
 *
 * @module
 */
import { Schema as S } from "effect"
import { Rpc, RpcGroup } from "effect/unstable/rpc"

// ============================================================================
// Schemas
// ============================================================================

export const InitiateHandoffResultSchema = S.Struct({
  handoffCode: S.String,
  /** Fully-qualified URL the CLI opens in a browser (env-aware app origin). */
  browserUrl: S.String,
  /** Suggested poll interval, milliseconds. */
  pollIntervalMs: S.Number,
  /** Absolute expiry, epoch milliseconds. */
  expiresAt: S.Number,
})
export type InitiateHandoffResult = typeof InitiateHandoffResultSchema.Type

/** Still waiting on the browser flow. */
export const HandoffPendingSchema = S.Struct({ status: S.Literal("pending") })
/** Unknown, consumed, or expired handoff code (also returned on verifier mismatch). */
export const HandoffExpiredSchema = S.Struct({ status: S.Literal("expired") })
/** Authorized — the session is minted and released exactly once. */
export const HandoffReadySchema = S.Struct({
  status: S.Literal("ready"),
  sessionId: S.String,
  sessionToken: S.String,
  workspaceId: S.String,
  versionId: S.String,
})

export const PollHandoffResultSchema = S.Union([
  HandoffPendingSchema,
  HandoffReadySchema,
  HandoffExpiredSchema,
])
export type PollHandoffResult = typeof PollHandoffResultSchema.Type

// ============================================================================
// Error
// ============================================================================

export class HandoffError extends S.TaggedErrorClass<HandoffError>()(
  "HandoffError",
  { message: S.String },
) {}

// ============================================================================
// RPC Group (no middleware — public)
// ============================================================================

export const HandoffRpc = RpcGroup.make(
  Rpc.make("initiate", {
    payload: {
      /** base64url(SHA-256(codeVerifier)) — the PKCE challenge. */
      codeChallenge: S.String,
    },
    success: InitiateHandoffResultSchema,
    error: HandoffError,
  }),

  Rpc.make("poll", {
    payload: {
      handoffCode: S.String,
      /** The PKCE verifier; proves the poller initiated this handoff. */
      codeVerifier: S.String,
    },
    success: PollHandoffResultSchema,
    error: HandoffError,
  }),
)
